import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	DestroyRef,
	ElementRef,
	inject,
	OnInit,
	signal,
	ViewChild,
	ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';

import { Language, TranslatePipe, TranslateService } from '@ngx-translate/core';
import dayjs from 'dayjs';
import { MarkdownModule } from 'ngx-markdown';
import { map, switchMap } from 'rxjs';

import { AlertComponent } from '@valk-nx/components/ui-alert/src/lib/alert.component';
import { CarouselComponent } from '@valk-nx/components/ui-carousel/src/lib/carousel';
import { ListGridComponent } from '@valk-nx/components/ui-list-grid/src/lib/list-grid';
import { MapComponent } from '@valk-nx/components/ui-map/src/lib/map';
import { MiniCarouselComponent } from '@valk-nx/components/ui-mini-carousel/src/lib/mini-carousel';
import { RatingComponent } from '@valk-nx/components/ui-rating/src/lib/rating';
import { SheetModule } from '@valk-nx/components/ui-sheet/src/lib/sheet.module';
import { TabMenuComponent } from '@valk-nx/components/ui-tab-menu/src/lib/tab-menu';
import { TabMenuItemDirective } from '@valk-nx/components/ui-tab-menu/src/lib/tab-menu.directive';
import { PersuadeCardComponent } from '@valk-nx/compositions/ui-persuade-card/src/lib/persuade-card.component';
import {
	fallbackLanguage,
	LANGUAGE,
	negativeHeader$,
} from '@valk-nx/core/lib/core';
import { BookingRedirectHelper } from '@valk-nx/helpers/lib/booking-redirect/booking-redirect.helper';
import { PriceHelper } from '@valk-nx/helpers/lib/price/price.helper';
import { ViewPortService } from '@valk-nx/services/viewport/src/lib/viewport.service';
import { AvailabilityWidgetComponent } from '@valk-nx/storyblok/components/availability-widget/src/lib/availability-widget';
import { StoryblokRootDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok-root.directive';
import { BOOKINGTOOL } from '@valk-nx/storyblok-services/src/lib/globals';
import { ContentService } from '@valk-nx/storyblok-services/src/lib/services/content.service';
import { MetadataService } from '@valk-nx/storyblok-services/src/lib/services/metadata.service';
import { StoryblokService } from '@valk-nx/storyblok-services/src/lib/services/storyblok.service';
import {
	DealStatus,
	HotelDealData,
} from '@valk-nx/storyblok-types/src/lib/types/content.interface';

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
	selector: 'sb-hotel-by-deal-detail',
	templateUrl: './hotel-by-deal-detail.component.html',
	imports: [
		AlertComponent,
		AvailabilityWidgetComponent,
		CarouselComponent,
		CommonModule,
		ListGridComponent,
		MapComponent,
		MarkdownModule,
		MiniCarouselComponent,
		PersuadeCardComponent,
		RatingComponent,
		RouterModule,
		SheetModule,
		TranslatePipe,
		TabMenuComponent,
		TabMenuItemDirective,
	],
})
export class HotelByDealDetailComponent
	extends StoryblokRootDirective
	implements OnInit
{
	private readonly sbService = inject(StoryblokService);
	private readonly contentService = inject(ContentService);
	private readonly metadataService = inject(MetadataService);
	private readonly viewportService = inject(ViewPortService);
	private readonly translate = inject(TranslateService);
	private readonly bookingtool = inject(BOOKINGTOOL);
	private readonly destroyRef = inject(DestroyRef);
	private readonly rootLanguage = inject<Language>(LANGUAGE);

	@ViewChild('contentRef', { read: ElementRef }) contentRef!: ElementRef;

	readonly currentLanguage = signal<string>(fallbackLanguage);
	readonly language: string = this.rootLanguage;

	readonly dealSlug = signal<string>('');
	readonly hotelSlug = signal<string>('');

	readonly isSmallTablet$ = this.viewportService.isSmallTablet$;

	readonly hotelDealData = signal<HotelDealData | null>(null);
	readonly isDataLoaded = computed(() => !!this.hotelDealData());

	readonly dealStatus = computed((): DealStatus => {
		const data = this.hotelDealData();
		if (!data) {
			return 'available';
		}

		switch (true) {
			case data.deal.isExpired:
				return 'expired';
			case data.deal.isAvailableSoon:
				return 'future';
			case !data.isAvailable:
				return 'unavailable';
			default:
				return 'available';
		}
	});

	readonly carouselImages = computed(() => {
		const data = this.hotelDealData();
		if (!data?.images?.length) {
			return [];
		}

		const { deal, hotel } = data;
		const dealAtHotelText = this.translate.instant(
			'storyblok.pages.hotel-by-deal.carousel.deal-at-hotel',
			{ hotelName: hotel.name },
		);

		return data.images.map((img) => ({
			image: { src: img.src, alt: img.alt },
			heading: deal.name,
			subHeading: dealAtHotelText,
			sticker: {
				src: deal.sticker.src,
				altText: deal.sticker.alt,
			},
			link: null,
		}));
	});

	readonly hotelImages = computed(() => {
		const images = this.hotelDealData()?.hotel?.images;
		return this.mapImages(images);
	});

	readonly dealImages = computed(() => {
		const images = this.hotelDealData()?.deal?.images;
		return this.mapImages(images);
	});

	readonly maxDate = computed(() => {
		const bookableTo = this.hotelDealData()?.deal?.bookable?.to;
		const defaultDate = dayjs().add(1, 'year');

		if (!bookableTo) {
			return defaultDate.toDate();
		}

		const bookableDate = dayjs(bookableTo);
		return bookableDate.isAfter(dayjs(), 'day')
			? bookableDate.toDate()
			: defaultDate.toDate();
	});

	readonly minDate = computed(() => {
		const bookableFrom = this.hotelDealData()?.deal?.bookable?.from;
		const defaultDate = dayjs().subtract(1, 'day');

		if (!bookableFrom) {
			return defaultDate.toDate();
		}

		const bookableDate = dayjs(bookableFrom);
		return bookableDate.isAfter(dayjs(), 'day')
			? bookableDate.toDate()
			: defaultDate.toDate();
	});

	constructor() {
		super();
		negativeHeader$.next(true);
		this.currentLanguage.set(
			this.translate.getCurrentLang() ?? this.translate.getFallbackLang(),
		);
	}

	ngOnInit() {
		this.loadHotelData();
	}

	private mapImages(images: { src: string; alt?: string }[] | undefined) {
		if (!images?.length) {
			return [];
		}

		return images.map((img) => ({
			src: img.src,
			altText: img.alt,
			width: 0,
			height: 0,
		}));
	}

	private loadHotelData(): void {
		this.sbService.translatedSlugs$
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				map((slugs) => slugs.find((slug) => slug.selected) ?? slugs[0]),
				switchMap((slug) => {
					this.extractRouteParameters(slug.path);
					return this.contentService.getHotelByDeal(
						this.dealSlug(),
						this.hotelSlug(),
						slug.lang.toLowerCase(),
					);
				}),
				map((response) => this.processHotelDealData(response.data)),
			)
			.subscribe((data) => this.hotelDealData.set(data));
	}

	private extractRouteParameters(path: string): void {
		const pathSegments = path.split('/');
		this.dealSlug.set(pathSegments.at(-2));
		this.hotelSlug.set(pathSegments.at(-1));
	}

	private processHotelDealData(
		data: HotelDealData,
	): HotelDealData & { pricingToolTip: { persons: number; rooms: number } } {
		data.bookingToolRedirectUrl = BookingRedirectHelper.getRedirectUrl(
			this.bookingtool,
			this.hotelSlug(),
			{
				dealGuid: data.deal.guid,
				languageCode: this.currentLanguage(),
			},
		);

		this.metadataService.updateMetaDataBatch(
			data,
			this.robotsNoFollow,
			this.robotsNoIndex,
		);

		if (data.images?.length === 0) {
			negativeHeader$.next(false);
		}

		return {
			...data,
			pricingToolTip: PriceHelper.getPricingTooltip(
				data.deal.occupancyRestrictions,
			),
		};
	}
}
